// ============================================================================
// 자동화 실행 로그 뷰 (CHG-047: REQ-V47-013)
// ============================================================================

import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useAutomationLogs } from '../../hooks/useAutomation';
import type { AutomationRule, AutomationLog } from '../../services/automationService';
import Spinner from '../ui/Spinner';

interface Props {
  projectId: string;
  rules: AutomationRule[];
}

const STATUS_CONFIG: Record<string, { label: string; bg: string; text: string }> = {
  SUCCESS: { label: '성공', bg: 'bg-green-100', text: 'text-green-700' },
  PARTIAL: { label: '부분', bg: 'bg-amber-100', text: 'text-amber-700' },
  FAILED: { label: '실패', bg: 'bg-red-100', text: 'text-red-700' },
  SKIPPED: { label: '스킵', bg: 'bg-gray-100', text: 'text-gray-500' },
};

const EVENT_LABELS: Record<string, string> = {
  PR_OPENED: 'PR 생성', PR_MERGED: 'PR 머지', PR_CLOSED: 'PR 닫힘',
  PR_APPROVED: '리뷰 승인', PR_CHANGES_REQUESTED: '변경 요청',
  PR_REVIEW_REQUESTED: '리뷰 요청', BRANCH_CREATED: '브랜치 생성',
};

export default function AutomationLogView({ projectId, rules }: Props) {
  const [filterRule, setFilterRule] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [page, setPage] = useState(1);
  const [expandedLog, setExpandedLog] = useState<string | null>(null);

  const params: Record<string, string> = { page: String(page), limit: '20' };
  if (filterRule) params.ruleId = filterRule;
  if (filterStatus) params.status = filterStatus;

  const { data, isLoading } = useAutomationLogs(projectId, params);

  return (
    <div className="space-y-3">
      {/* Filters */}
      <div className="flex items-center gap-2">
        <select value={filterRule} onChange={(e) => { setFilterRule(e.target.value); setPage(1); }} className="px-2 py-1.5 text-sm border border-gray-300 rounded-lg">
          <option value="">전체 규칙</option>
          {rules.map((r) => <option key={r.ruleId} value={r.ruleId}>{r.name}</option>)}
        </select>
        <select value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }} className="px-2 py-1.5 text-sm border border-gray-300 rounded-lg">
          <option value="">전체 결과</option>
          {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-8"><Spinner className="size-6" /></div>
        ) : !data?.data?.length ? (
          <div className="py-8 text-center text-sm text-gray-400">실행 로그가 없습니다.</div>
        ) : (
          <div>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-sm font-medium text-gray-500">
                  <th className="text-left px-4 py-2">시간</th>
                  <th className="text-left px-4 py-2">규칙</th>
                  <th className="text-center px-4 py-2">트리거</th>
                  <th className="text-center px-4 py-2">결과</th>
                  <th className="text-center px-4 py-2 w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.data.map((log) => {
                  const cfg = STATUS_CONFIG[log.status] ?? STATUS_CONFIG.SKIPPED;
                  const isExpanded = expandedLog === log.logId;
                  return (
                    <LogRow key={log.logId} log={log} cfg={cfg} isExpanded={isExpanded} onToggle={() => setExpandedLog(isExpanded ? null : log.logId)} />
                  );
                })}
              </tbody>
            </table>
            {/* Pagination */}
            {data.total > 20 && (
              <div className="flex items-center justify-center gap-2 py-3 border-t border-gray-100">
                <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1} className="px-2 py-1 text-sm text-gray-600 border border-gray-200 rounded disabled:opacity-40">이전</button>
                <span className="text-sm text-gray-500">{page} / {Math.ceil(data.total / 20)}</span>
                <button onClick={() => setPage(page + 1)} disabled={page * 20 >= data.total} className="px-2 py-1 text-sm text-gray-600 border border-gray-200 rounded disabled:opacity-40">다음</button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function LogRow({ log, cfg, isExpanded, onToggle }: {
  log: AutomationLog;
  cfg: { label: string; bg: string; text: string };
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const time = new Date(log.executedAt).toLocaleString('ko-KR', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  return (
    <>
      <tr className="hover:bg-gray-50 cursor-pointer" onClick={onToggle}>
        <td className="px-4 py-2 text-sm text-gray-500">{time}</td>
        <td className="px-4 py-2 text-sm font-medium text-gray-900">{log.rule.name}</td>
        <td className="px-4 py-2 text-center text-sm text-gray-600">{EVENT_LABELS[log.triggerEvent] ?? log.triggerEvent}</td>
        <td className="px-4 py-2 text-center">
          <span className={`px-1.5 py-0.5 text-[11px] font-medium rounded ${cfg.bg} ${cfg.text}`}>{cfg.label}</span>
        </td>
        <td className="px-4 py-2 text-center text-gray-400">
          {isExpanded ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />}
        </td>
      </tr>
      {isExpanded && (
        <tr>
          <td colSpan={5} className="px-4 py-3 bg-gray-50">
            <div className="space-y-2 text-sm">
              <div>
                <span className="font-medium text-gray-600">조건 충족:</span>{' '}
                <span className={log.conditionsMet ? 'text-green-600' : 'text-red-500'}>{log.conditionsMet ? '✅ 예' : '❌ 아니오'}</span>
              </div>
              {log.actionsResult.length > 0 && (
                <div>
                  <span className="font-medium text-gray-600">액션 결과:</span>
                  <div className="mt-1 space-y-0.5 ml-3">
                    {log.actionsResult.map((ar, i) => (
                      <div key={i} className="flex items-center gap-1.5">
                        <span>{ar.success ? '✅' : '❌'}</span>
                        <span className="text-gray-600">{ar.type}:</span>
                        <span className="text-gray-500">{ar.message}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {log.errorMessage && (
                <div className="text-red-600">
                  <span className="font-medium">에러:</span> {log.errorMessage}
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
